<!-- TODO: Performance may not be great especially on mobile, since
	Vue rerenders the parent component when a slot in one of its children
	changes (Vue 3.0 should hopefully change that). Since Vuetify uses
	slots a lot internally, this can get slow on e.g. the input event
	on a v-text-field, like we have here. A workaround can be to avoid
	v-model and instead use :value with @changed instead so the event is
	triggered only on blur.
-->

<template>
  <v-container
    fluid
    class="login-container"
  >
    <v-layout
      column
      align-center
      :style="{'margin-top': $vuetify.breakpoint.smAndUp ? '100px' : null}"
    >
      <!-- Logo -->
      <a href="https://www.myshopradio.de">
        <img
          src="/static/logo_myshopradio_tv_black.svg"
          height="56px"
          class="clickable"
          :class="{
            'login-logo-desktop': $vuetify.breakpoint.smAndUp,
            'login-logo-mobile': $vuetify.breakpoint.xs,
          }"
        >
      </a>
      <!-- Title -->
      <h1
        v-if="$vuetify.breakpoint.smAndUp"
        class="display-3 grey--text text--darken-4 my-3"
        style="font-weight: lighter"
      >
        {{ $t('signupPageTitle') }}
      </h1>
      <!-- Login Link + Usage Hint-->
      <v-layout
        column
        justify-center
        class="mb-3"
        style="font-size: 18px"
      >
        <span>
          <span
            class="grey--text text--darken-3"
          >{{ $t('loginPreQuestion') }}&nbsp;</span>
          <a
            href="javascript:void(0)"
            style="text-decoration: none"
            @click="goToLogin()"
          >{{ $t('signupToLogin') }}</a>
        </span>
      </v-layout>
      <!-- Form -->
      <v-card
        v-if="!submitted"
        flat
        color="transparent"
        class="signup-form-card"
        width="100%"
        :style="{'max-width': $vuetify.breakpoint.smAndUp ? '720px' : null}"
      >
        <v-card-actions>
          <v-layout justify-center>
            <v-form
              ref="form"
              v-model="formIsValid"
              class="grow-1"
            >
              <v-layout
                :column="$vuetify.breakpoint.xsOnly"
                wrap
              >
                <v-text-field
                  ref="firstNameInput"
                  v-model="firstName"
                  required
                  :label="$t('firstName')"
                  :rules="[rules.required]"
                  :class="{'mr-3': $vuetify.breakpoint.smAndUp}"
                  @keyup.esc="firstName = ''"
                />
                <v-text-field
                  v-model="lastName"
                  :label="$t('lastName')"
                  :rules="[rules.required]"
                  append-icon="person"
                  required
                  @keyup.esc="lastName = ''"
                />
              </v-layout>
              <v-text-field
                v-model="company"
                :label="$t('company')"
                append-icon="store"
                :rules="[rules.required]"
                required
                @keyup.esc="company = ''"
              />
              <v-layout
                :column="$vuetify.breakpoint.xsOnly"
                wrap
              >
                <v-layout
                  column
                  :class="{'mr-3': $vuetify.breakpoint.smAndUp}"
                  class="grow-1"
                >
                  <v-text-field
                    v-model="email"
                    :label="$t('email')"
                    append-icon="mail"
                    :rules="[rules.email]"
                    :loading="checkingEmail"
                    @change="onEmailChanged"
                    @keyup.esc="email = ''"
                  />
                  <!-- Generic Email Domain Warning -->
                  <v-slide-y-transition>
                    <v-alert
                      v-if="isGenericEmailDomain"
                      value="true"
                      type="warning"
                      outline
                      style="word-break: break-word"
                    >
                      <strong>{{ $t('signupEmailWarning_1') }}</strong>
                      {{ $t('signupEmailWarning_2') }}
                    </v-alert>
                  </v-slide-y-transition>
                </v-layout>
                <v-text-field
                  v-model="phone"
                  :label="$t('phone')"
                  append-icon="phone"
                  :rules="[rules.phone]"
                  required
                  class="grow-1"
                  :style="{
                    'max-width': $vuetify.breakpoint.smAndUp ? '300px' : null,
                  }"
                  @keyup.esc="phone = ''"
                />
              </v-layout>
              <v-layout
                class="mt-4"
                justify-start
              >
                <v-hover>
                  <v-btn
                    slot-scope="{hover}"
                    round
                    large
                    :block="$vuetify.breakpoint.xs"
                    :dark="hover"
                    :flat="hover"
                    :outline="!hover"
                    :class="{'msr--text': !hover, msr: hover}"
                    :disabled="!formIsValid"
                    :loading="busy"
                    @click="submit()"
                  >
                    {{ $t('loginToSignup') }}
                  </v-btn>
                </v-hover>
              </v-layout>
            </v-form>
          </v-layout>
        </v-card-actions>
      </v-card>
      <!-- Success Message after Submission -->
      <v-card v-else>
        <v-card-actions>
          <v-layout
            row
            justify-center
          >
            <v-icon
              style="font-size: 5em"
              color="success"
            >
              check
            </v-icon>
            <v-layout column>
              <div class="grey--text text--darken-2 ma-4">
                {{ $t('demoAccountCreated') }}
                <p class="mt-2 mb-0 msr--text">
                  {{ submittedToEmail }}
                </p>
              </div>
              <v-layout justify-end>
                <v-btn
                  flat
                  round
                  color="msr"
                  @click="goToLogin()"
                >
                  {{ $t('close') }}
                </v-btn>
              </v-layout>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </v-card>
      <!-- Confirmation to data privacy etc. info -->
      <v-layout
        align-center
        class="mt-5 grey--text text-xs-center"
        style="max-width: 720px"
      >
        <small>* {{ $t('signupLoginConfirmationByClickInfo') }}</small>
      </v-layout>
    </v-layout>
    <div style="min-height: 60px" />
  </v-container>
</template>

<script>
import dayjs from 'dayjs';

export default {
  data() {
    const vm = this;
    return {
      formIsValid: false,
      firstName: '',
      lastName: '',
      company: '',

      email: '',
      isGenericEmailDomain: false,
      checkingEmail: false,
      submittedToEmail: '',

      phone: '',
      rules: {
        required(value) {
          return !!value ? true : vm.$t('isRequired');
        },
        requiredSilent(value) {
          return !!value ? true : '';
        },
        email(text) {
          return vm.isValidEmailAddress(text) ? true : vm.$t('invalidEmail');
        },
        phone: (value) => {
          let failure = '';

          const numChars = value.replace(' ', '').length;
          const minNumChars = 8;
          const maxNumChars = 24;
          if (numChars < minNumChars) {
            failure += vm.$t('tooShort') + '. ';
          }
          if (numChars > maxNumChars) {
            failure += vm.$t('tooLong') + '. ';
          }

          const pattern = /^[0-9\+\/ \-]+$/;
          const invalid = !value || !pattern.test(value);
          if (invalid) {
            failure += vm.$t('invalidPhone') + '. ';
          }

          return (!invalid && failure === '') || failure;
        },
      },
      busy: false,
      submitted: false,
    };
  },
  computed: {
    isUsingWebview() {
      return this.$store.state.common.isUsingWebview;
    },
    formHasBeenChanged() {
      return (
        this.firstName !== '' ||
        this.lastName !== '' ||
        this.company !== '' ||
        this.email !== ''
      );
    },
  },
  mounted() {
    this.$refs.firstNameInput.focus();
  },
  methods: {
    isValidEmailAddress(text) {
      const regexUnicodeEmail =
        /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
      return regexUnicodeEmail.test(text);
    },
    onEmailChanged(email) {
      if (!this.isValidEmailAddress(email)) {
        this.isGenericEmailDomain = false;
        return;
      }
      this.checkingEmail = true;
      this.$api
        .checkIsEmailDomainGeneric(email)
        .then((response) => Boolean(response.data))
        .then((isGeneric) => (this.isGenericEmailDomain = isGeneric))
        .finally(() => (this.checkingEmail = false));
    },
    reset() {
      this.firstName = '';
      this.lastName = '';
      this.company = '';
      this.email = '';
      this.submittedToEmail = '';
      this.phone = '';
    },
    genericErrorFeedback_() {
      this.$bus.$emit('alert-error', {
        message: 'genericFailedInfo',
        translate: true,
        timeout: 8000,
      });
    },
    submit() {
      if (!this.$refs.form.validate()) {
        return;
      }

      const payload = this.getPayload_();
      this.busy = true;
      this.$api
        .submitSignUpForm(payload)
        .then((response) => {
          this.busy = false;
          this.submitted = true;
          this.reset();
          this.submittedToEmail = payload.email;
        })
        .catch((error) => {
          this.genericErrorFeedback_();

          this.busy = false;
        });
    },
    getPayload_() {
      const now = dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss');
      let payload = {
        first_name: this.firstName,
        last_name: this.lastName,
        company: this.company,
        email: this.email,
        phone: this.phone,
        dsgvo_compliance_given_at: now,
      };
      return payload;
    },
    goToLogin() {
      let confirmed = true;
      if (this.formHasBeenChanged) {
        confirmed = confirm(this.$t('signupFormChangedRouteConfirm'));
      }
      if (confirmed) {
        this.$router.push('/login');
      }
    },
  },
};
</script>

<style>
.multiline-input-group-label.input-group label {
  overflow: initial;
  white-space: normal;
  line-height: 20px;
}

.v-radio-group-error-mt-1.input-group.radio-group
  .input-group__details
  .input-group__messages
  .input-group__error {
  margin-top: 8px;
}
</style>

<style scoped>
@media (max-width: 599px) {
  .login-container {
    padding: 0;
  }
}

.login-logo-desktop {
  position: fixed;
  top: 0;
  left: 0;
  margin: 60px 0 0 60px;
}
.login-logo-mobile {
  position: relative;
  top: -24px;
}

.signup-form-card {
  z-index: 4; /* Should be above the toolbar. */
}
</style>

<style>
/* Disable error 'wiggle' animation on inputs. */
.v-input--has-state.error--text .v-label {
  animation: none !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/Signup.vue